import { defineStore } from 'pinia';

import OrderAPI from '~/api/order';
import {
  COUPON_DATA_STORAGE_KEY,
  COUPON_DATA_VERSION,
} from '~/api/plugins/coupon';
import type { ResponseError } from '~/api/utils/types';

export const useCouponStore = defineStore('coupon', () => {
  const t = useNuxtApp().$i18n.t;
  const orderStore = useOrderStore();
  const modalStore = useModalStore();

  const isLoading = ref(false);
  const coupon = ref('');
  const isActivated = ref(false);

  const setCouponRequest = reactive(
    useRequest(OrderAPI.setCoupon, () => ({ body: { coupon: coupon.value } })),
  );
  const deleteCouponRequest = reactive(
    useRequest(OrderAPI.deleteCoupon, () => ({
      body: { coupon: coupon.value },
    })),
  );

  const applyCoupon = async () => {
    if (isActivated.value || isLoading.value) return;
    isLoading.value = true;

    try {
      const couponValue = coupon.value;
      const couponResponse = await setCouponRequest.execute();
      if (!couponResponse) return;

      if (typeof localStorage !== 'undefined') {
        localStorage[COUPON_DATA_STORAGE_KEY] = JSON.stringify({
          version: COUPON_DATA_VERSION,
          key: couponResponse.name,
          value: couponValue,
          code: couponResponse.value,
        });
      }

      await orderStore.fetchCurrentOrder();

      isActivated.value = true;
    } catch (e) {
      modalStore.showError({
        title: getFetchErrorMessage(
          e as ResponseError,
          t('store.coupon.message.apply_error'),
        ),
        action: t('store.coupon.message.action'),
      });
    } finally {
      isLoading.value = false;
    }
  };

  const clearCoupon = () => {
    if (typeof localStorage !== 'undefined')
      localStorage.removeItem(COUPON_DATA_STORAGE_KEY);

    coupon.value = '';
    isActivated.value = false;
  };

  const deleteCoupon = async () => {
    if (!isActivated.value || isLoading.value) return;
    isLoading.value = true;

    try {
      const couponResponse = await deleteCouponRequest.execute();
      if (!couponResponse) return;

      clearCoupon();

      await orderStore.fetchCurrentOrder();
    } catch (e) {
      modalStore.showError({
        title: getFetchErrorMessage(
          e as ResponseError,
          t('store.coupon.message.cancel_error'),
        ),
        action: t('store.coupon.message.action'),
      });
    } finally {
      isLoading.value = false;
    }
  };

  onMounted(() => {
    if (localStorage[COUPON_DATA_STORAGE_KEY]) {
      const cachedData = JSON.parse(localStorage[COUPON_DATA_STORAGE_KEY]);
      if (cachedData.version === COUPON_DATA_VERSION) {
        coupon.value = cachedData.value;
        isActivated.value = true;
      }
    }
  });

  return {
    coupon,
    isLoading,
    isActivated,
    applyCoupon,
    deleteCoupon,
    clearCoupon,
  };
});
